import React, {
  useCallback, useEffect, useState, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { compose } from 'recompose';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { Button, Message } from 'semantic-ui-react';
import { CSVLink } from 'react-csv';
import { graphql } from 'react-apollo';
import LocationList from '../views/AuctionLocation/LocationList';
import Search from '../views/Shared/Search';
import AdditionalDetailsLocation from '../views/AuctionLocation/AdditionalDetailsLocation';
import withPagination from './WithPagination';
import { setSearch } from '../data/searchField/actions';
import { setPage } from '../data/page/actions';
import auctionLocationQuery from '../graphql/auctionLocationQuery';
import LoadingDataEnhancer from './LoadingData';
import {
  auctionLocationCSV,
  auctionLocationHeader,
  exportFileName,
} from '../views/AuctionLocation/AuctionLocationsExporter';
import AuctionLocationForm, {
  AuctionLocationAction,
} from '../views/AuctionLocation/AuctionLocationForm/AuctionLocationForm';
import createOrUpdateAuctionLocationMutation from '../graphql/updateAuctionLocationMutation';
import { utcMoment } from '../utilities/dateUtils';
import { LocationInfoAction } from '../views/AuctionLocation/LocationListRow';
import { hasRole } from '../utilities/user';
import client from '../apollo';

export const messageSuccess = {
  Create: 'Successfully Created New Auction Location',
  Update: 'Successfully Updated Auction Location',
  Delete: 'Successfully Deleted Auction Location',
};

export const BoolStringValue = {
  Truthy: '1',
  Falsy: '0',
};

export const CreateUpdateErrorMessage = 'The system encountered an unexpected error while attempting to create or update the location.';

const AuctionLocation = props => {
  const {
    createOrUpdateAuctionLocation,
    searchFields,
    user,
    data,
    history,
    setSearchFields,
    setPage: setPageAction,
  } = props;
  const isAuctionLocationEditor = hasRole('auction_location_editor', 'roles')(user);
  const [locationData, setLocationData] = useState([]);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [flashMessage, setFlashMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [isNegativeFlashMsg, setIsNegativeFlashMsg] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [locationAction, setLocationAction] = useState(
    AuctionLocationAction.Create
  );
  const [errorMessage, setErrorMessage] = useState('');
  const [isOpenModalAdditionalDetails, setIsOpenModalAdditionalDetails] = useState(false);
  const [locationDetail, setLocationDetail] = useState({});

  useEffect(() => {
    setLocationData(data.apmsLocationList);
  }, [data]);

  const [csvData, setCsvData] = useState([]);
  const [csvReady, setCsvReady] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const csvLinkRef = useRef();

  const onEditLocationClick = location => {
    const formattedLocation = {
      locationId: location.locationId || '',
      locationName: location.locationName || '',
      locationCode: location.locationCode || '',
      externalAuctionCode: location.auctionedgeCode || '',
      auctionSystem: location.auctionSystem
        ? location.auctionSystem.toUpperCase()
        : '',
      purchasesEnabled: location.purchaseEnabled || false,
      acceptsReturn: location.acceptsReturns || false,
      inspectionsEnabled: location.inspectionsEnabled || false,
      dsPaidPsiEnabled: location.dsPaidPsiEnabled || false,
      saleDays: location.saleDays ? location.saleDays.split(/\s*,\s*/) : [],
      titleDays: location.titleAllowanceDays || '',
      launchDate: location.launchDate || '',
      address: location.locationStreet || '',
      city: location.locationCity || '',
      state: location.locationState || '',
      zip: location.locationZip || '',
      locationArea: location.locationArea || '',
      dealerNumber: location.dealerNumber || '',
      supplierNumber: location.supplierNumber || '',
      macolaInventoryCode: location.macolaInventoryCode || '',
      returnInstructions: location.returnInstructions || '',
      auctionNotes: location.auctionNotes || '',
      returnEmails: location.returnEmails || '',
      externalAuctionEmails: location.externalAuctionEmails || '',
      additionalEmails: location.additionalEmails || '',
    };
    setSelectedLocation(formattedLocation);
    setLocationAction(AuctionLocationAction.Update);
    setShowLocationModal(true);
  };

  const onSelectAuctionLocation = (location, action) => {
    // TODO handle Actions
    switch (action) {
      case LocationInfoAction.EDIT:
        onEditLocationClick(location);
        break;
      case LocationInfoAction.ADDITIONAL_DETAIL:
        setIsOpenModalAdditionalDetails(true);
        setLocationDetail(location);
        break;
      case LocationInfoAction.CONTACTS:
        history.push({
          pathname: `/auction_location/auction_contact/${location.locationId}`,
        });
        break;
      default:
    }
  };

  const auctionLocationSearchQuery = event => {
    const { auctionLocationSearch } = event.target;
    const variableArguments = {
      auctionLocationSearch: auctionLocationSearch.value,
    };
    setSearchFields({ ...variableArguments });
    setPageAction({});

    data.refetch(variableArguments);

    setLocationData(data.apmsLocationList);
  };

  const onFailedSubmit = error => {
    setLoading(false);
    let errorMsg;
    if (typeof error === 'string') {
      errorMsg = error;
    } else if (Array.isArray(error)) {
      errorMsg = error;
    } else if (error && error.message) {
      errorMsg = error.message;
    } else {
      errorMsg = CreateUpdateErrorMessage;
    }
    setErrorMessage(errorMsg);
    setIsNegativeFlashMsg(true);
  };

  const onCloseHandler = () => {
    setShowLocationModal(false);
    setSelectedLocation(null);
    setErrorMessage('');
    setLocationAction(AuctionLocationAction.Create);
  };

  const onOpenLocationModal = () => {
    setShowLocationModal(true);
  };

  const getAuctionLocationActionInput = values => {
    const parseTruthyString = isTruthy => (isTruthy ? BoolStringValue.Truthy : BoolStringValue.Falsy);
    const locationDataInput = {
      initials: values.locationCode,
      name: values.locationName,
      inspectionsEnabled: parseTruthyString(!!values.inspectionsEnabled),
      acceptsReturns: parseTruthyString(!!values.acceptsReturn),
      purchasesEnabled: parseTruthyString(!!values.purchasesEnabled),
      returnInstructions: values.returnInstructions
        ? values.returnInstructions.toString()
        : '',
      returnEmails: values.returnEmails ? values.returnEmails.toString() : '',
      externalAuctionEmails: values.externalAuctionEmails
        ? values.externalAuctionEmails.toString()
        : '',
      additionalEmails: values.additionalEmails ? values.additionalEmails.toString() : '',
      street: values.address,
      city: values.city,
      state: values.state,
      zip: values.zip ? values.zip.toString() : '',
      auctionedgeCode: values.externalAuctionCode,
      saleDays: values.saleDays ? values.saleDays.join(',') : '',
      supplierNumber: values.supplierNumber
        ? values.supplierNumber.toString()
        : '',
      macolaInventoryCode: values.macolaInventoryCode,
      auctionNotes: values.auctionNotes,
      launchDate: utcMoment(values.launchDate),
      titleAllowanceDays: Number(values.titleDays),
      dsPaidPsiEnabled: parseTruthyString(!!values.dsPaidPsiEnabled),
      dealerNumber: values.dealerNumber ? values.dealerNumber.toString() : '',
      locationArea: values.locationArea,
      auctionOs: BoolStringValue.Falsy,
      g2gConverted: BoolStringValue.Falsy,
      bscLocation: parseTruthyString(values.auctionSystem === 'BSC'),
      xcira: parseTruthyString(values.auctionSystem === 'XCIRA'),
      auctionSystem: values.auctionSystem,
    };
    return { auctionLocation: { ...locationDataInput } };
  };

  const onExcuteMutationAuctionLocation = async(
    mutation,
    mutationResultName,
    values,
    action
  ) => {
    try {
      const input = getAuctionLocationActionInput(values);
      const repsonse = await mutation({
        variables: { ...input },
        errorPolicy: 'all',
      }).catch(error => {
        throw new Error(error);
      });
      const { success, errors, msg } = repsonse.data[mutationResultName];

      if (success) {
        setShowLocationModal(false);
        setFlashMessage(messageSuccess[action]);
        setIsNegativeFlashMsg(false);
        setLoading(false);
        setPageAction({});
        data.refetch();
      } else {
        onFailedSubmit(errors && errors.length ? errors : msg || CreateUpdateErrorMessage);
      }
    } catch (error) {
      onFailedSubmit(error);
    }
  };

  const onFormSubmitHandler = useCallback(
    async values => {
      try {
        setLoading(true);
        const { action } = values;
        switch (action) {
          case AuctionLocationAction.Create:
          case AuctionLocationAction.Update:
            await onExcuteMutationAuctionLocation(
              createOrUpdateAuctionLocation,
              'createOrUpdateAuctionLocation',
              values,
              action
            );
            break;
          case AuctionLocationAction.Delete:
            break;
          default:
            break;
        }
      } catch (error) {
        onFailedSubmit(error);
      }
    },
    [createOrUpdateAuctionLocation, onFailedSubmit]
  );

  const handleAllLocationButton = () => {
    history.push('/auction_location/all_auction_contact');
  };

  const handleExportCSV = async() => {
    if (isExporting) return; // Prevent multiple clicks
    setIsExporting(true);
    try {
      const result = await client.query({
        query: auctionLocationQuery,
        variables: {
          isPaginationActive: false,
          auctionLocationSearch: data.variables.auctionLocationSearch || '',
        },
        fetchPolicy: 'network-only',
      });
      const list = result.data?.apmsLocationList || [];
      setCsvData(auctionLocationCSV(list));
      setCsvReady(true);

      // wait for state updated before trigger download
      setTimeout(() => {
        csvLinkRef.current.link.click();
        setCsvReady(false);
        setIsExporting(false);
      }, 0);
    } catch (err) {
      setFlashMessage('Export failed!');
      setIsNegativeFlashMsg(true);
      setIsExporting(false);
    }
  };

  return (
    <div>
      {flashMessage && (
        <Message
          onDismiss={() => setFlashMessage(null)}
          content={flashMessage}
          positive
          negative={isNegativeFlashMsg}
        />
      )}
      <div className='d-flex-between'>
        <div className='width-full'>
          <Search
            onSubmit={auctionLocationSearchQuery}
            searchFields={searchFields}
            searchOptions={['auctionLocationSearch']}
            noPlaceholder
          />
        </div>
        <div className='button-max-content'>
          <Button disabled={!isAuctionLocationEditor} color='green' inverted onClick={onOpenLocationModal}>
            New Location
          </Button>
        </div>
      </div>

      <div className='d-flex-end'>
        <Button
          basic
          icon='file excel'
          content={isExporting ? 'Exporting...' : 'Export to CSV'}
          onClick={handleExportCSV}
          disabled={isExporting}
        />
        { csvReady && (
        <CSVLink
          data={csvData}
          headers={auctionLocationHeader}
          filename={exportFileName}
          enclosingCharacter='"'
          ref={csvLinkRef}
          style={{ display: 'none' }}
        />
        )}
        <Button
          basic
          icon='address book outline'
          content='View all contacts'
          onClick={handleAllLocationButton}
        />
      </div>

      <LocationList
        auctionLocationList={locationData}
        onSelectAuctionLocation={onSelectAuctionLocation}
        isAuctionLocationEditor={isAuctionLocationEditor}
      />

      {isOpenModalAdditionalDetails && (
        <AdditionalDetailsLocation
          isOpen={isOpenModalAdditionalDetails}
          location={locationDetail}
          onCloseHandler={() => setIsOpenModalAdditionalDetails(false)}
        />
      )}
      <AuctionLocationForm
        isModalOpen={showLocationModal}
        isLoading={loading}
        submitError={errorMessage}
        locationAction={locationAction}
        selectedLocation={selectedLocation}
        onSubmitHandler={onFormSubmitHandler}
        onCloseHandler={onCloseHandler}
        onCloseError={() => setErrorMessage('')}
      />
    </div>
  );
};

AuctionLocation.propTypes = {
  createOrUpdateAuctionLocation: PropTypes.func.isRequired,
  searchFields: PropTypes.shape({}).isRequired,
  user: PropTypes.shape({}).isRequired,
  data: PropTypes.shape({
    apmsLocationList: PropTypes.arrayOf(PropTypes.shape({})),
    refetch: PropTypes.func,
    variables: PropTypes.shape({
      auctionLocationSearch: PropTypes.string,
    }),
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  setSearchFields: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
};

const mapDispatchToProps = dispatch => ({
  setSearchFields: fields => {
    dispatch(setSearch(fields));
  },
  setPage: () => {
    dispatch(setPage());
  },
});

const mapStateToProps = state => {
  const {
    data: { searchFields },
  } = state;
  return {
    searchFields,
  };
};

export default compose(
  withRouter,
  withPagination(auctionLocationQuery, 'apmsLocationList'),
  LoadingDataEnhancer,
  graphql(createOrUpdateAuctionLocationMutation, {
    name: 'createOrUpdateAuctionLocation',
  }),
  connect(mapStateToProps, mapDispatchToProps)
)(AuctionLocation);
